import { useSyncExternalStore } from 'react'
import type { Document, SourceId } from '@/core/document'
import type { ExportOptions } from '@/core/export/types'
import type { SourceImage } from '@/core/image'

export interface ExactQualitySizes {
  jpegBytes: number
  pngBytes: number
}

interface ExactQualitySizeEntry extends ExactQualitySizes {
  document: Document
  sources: ReadonlyMap<SourceId, SourceImage>
  jpegQuality: number
  resizeMode: ExportOptions['resizeMode']
  customLongestSide: number | null
}

interface QualitySizeSnapshot {
  revision: number
  entry: ExactQualitySizeEntry | null
}

let snapshot: QualitySizeSnapshot = { revision: 0, entry: null }
const listeners = new Set<() => void>()

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

function getSnapshot(): QualitySizeSnapshot {
  return snapshot
}

export function cacheExactQualitySizes(
  document: Document,
  sources: ReadonlyMap<SourceId, SourceImage>,
  options: ExportOptions,
  sizes: ExactQualitySizes,
): void {
  if (!Number.isInteger(sizes.jpegBytes) || sizes.jpegBytes <= 0
    || !Number.isInteger(sizes.pngBytes) || sizes.pngBytes <= 0) {
    throw new Error('Exact quality sizes must contain positive integer JPEG and PNG byte counts')
  }
  snapshot = {
    revision: snapshot.revision + 1,
    entry: {
      document,
      sources,
      jpegQuality: options.jpegQuality,
      resizeMode: options.resizeMode,
      customLongestSide: options.customLongestSide,
      ...sizes,
    },
  }
  listeners.forEach((listener) => listener())
}

export function exactQualitySizesFor(
  document: Document | null,
  sources: ReadonlyMap<SourceId, SourceImage>,
  options: ExportOptions,
): ExactQualitySizes | null {
  const entry = snapshot.entry
  return document && entry
    && entry.document === document
    && entry.sources === sources
    && entry.jpegQuality === options.jpegQuality
    && entry.resizeMode === options.resizeMode
    && entry.customLongestSide === options.customLongestSide
    ? { jpegBytes: entry.jpegBytes, pngBytes: entry.pngBytes }
    : null
}

export function useExactQualitySizes(
  document: Document | null,
  sources: ReadonlyMap<SourceId, SourceImage>,
  options: ExportOptions,
): ExactQualitySizes | null {
  useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
  return exactQualitySizesFor(document, sources, options)
}
